import { Enums as csEnums } from '@cornerstonejs/core';

const bridge = (typeof window !== 'undefined' && (window as any).directViewerBridge) || null;

type SyncEvent =
  | {
      type: 'voi';
      viewportId?: string;
      range: { lower: number; upper: number };
      viewportSlot?: number;
      orientation?: string;
      userInitiated?: boolean;
    }
  | {
      type: 'camera';
      viewportId: string;
      camera: any;
      viewportSlot?: number;
      orientation?: string;
      userInitiated?: boolean;
    }
  | {
      type: 'scroll';
      viewportId: string;
      imageIndex: number;
      viewportSlot?: number;
      orientation?: string;
      userInitiated?: boolean;
    }
  | {
      type: 'zoompan';
      viewportId: string;
      zoom?: number;
      pan?: { x: number; y: number };
      parallelScale?: number;
      position?: [number, number, number];
      focalPoint?: [number, number, number];
      viewportSlot?: number;
      orientation?: string;
      userInitiated?: boolean;
    }
  | { type: 'tool'; activeTool: string };

export class ElectronSyncService {
  private propagating = false;
  private applying = false; // echo suppression
  private unsubscribes: Array<() => void> = [];
  private services: any;
  private commandsManager: any;
  private lastSent: Record<string, number> = {};
  private pendingSends: Record<
    string,
    { evt: SyncEvent; timeout: ReturnType<typeof setTimeout> | null }
  > = {};
  private throttleMs = 50; // adjust if needed
  private debug = false;
  private samplers: Array<() => void> = [];
  private globalPollTimer: any = null;
  private stats = {
    sent: { voi: 0, camera: 0, zoompan: 0, scroll: 0, tool: 0 },
    recv: { voi: 0, camera: 0, zoompan: 0, scroll: 0, tool: 0 },
  };
  private debugOverlayEl: HTMLElement | null = null;
  private _lastZoomPan: Record<
    string,
    {
      zoom?: number;
      pan?: { x: number; y: number };
      parallelScale?: number;
      position?: [number, number, number];
      focalPoint?: [number, number, number];
    }
  > = {};
  private _quietUntil: Record<string, number> = {};
  private _userActive: Record<string, boolean> = {};
  private _programmatic: Record<string, boolean> = {};
  private _pendingProgrammaticExit: Record<string, boolean> = {};
  private _lastUserAt: Record<string, number> = {};
  private _userGraceMs = 600;
  private _lastViewportOrientation: Record<string, string> = {};
  private _lastSentTool: string | null = null;
  private _pendingToolActivation: string | null = null;

  constructor(servicesManager: any) {
    this.services = servicesManager?.services || {};
    this.commandsManager =
      servicesManager?.commandsManager ||
      servicesManager?._commandsManager ||
      (typeof window !== 'undefined' ? (window as any)?.commandsManager : null);
    try {
      this.debug = localStorage.getItem('dv:sync:debug') === '1';
    } catch {}
    try {
      if (
        !this.debug &&
        typeof window !== 'undefined' &&
        window.location &&
        window.location.search
      ) {
        const sp = new URLSearchParams(window.location.search);
        if (sp.get('dvSyncDebug') === '1') {
          this.debug = true;
          localStorage.setItem('dv:sync:debug', '1');
        }
      }
    } catch {}
    this.enable();
  }

  enable() {
    if (!bridge) return;
    if (this.unsubscribes.length) return;
    this.propagating = true;
    bridge.setPropagating?.(true);
    this.attachListeners();
    bridge.onIncomingSync?.((evt: SyncEvent) => this.applyIncoming(evt));
    this.startGlobalPolling();
    try {
      this.debug = localStorage.getItem('dv:sync:debug') === '1' || this.debug;
    } catch {}
    if (this.debug) this.ensureDebugOverlay();
  }
  disable() {
    if (!bridge) return;
    bridge.setPropagating?.(false);
    this.propagating = false;
    this._clearPendingSends();
    this.unsubscribes.forEach(fn => {
      try {
        fn();
      } catch {}
    });
    this.unsubscribes = [];
    this.stopGlobalPolling();
    this.removeDebugOverlay();
  }

  private attachListeners() {
    const { cornerstoneViewportService, viewportGridService, toolbarService } = this.services;
    if (!cornerstoneViewportService) return;

    const addForViewport = (viewportId: string) => {
      const info = cornerstoneViewportService.getViewportInfo?.(viewportId);
      const el = info?.getElement?.();
      const viewport = cornerstoneViewportService.getCornerstoneViewport?.(viewportId);
      try {
        (bridge as any)?.log?.('sync-attach', { viewportId, hasEl: !!el, hasVp: !!viewport });
      } catch {}
      if (!el) return;
      this._enterProgrammatic(viewportId);
      this._squelch(viewportId, 800);
      this._userActive[viewportId] = false;
      // Desativa a propagação de VOI/windowlevel não faz nada
      const voiListener = (_e: any) => {
        // Não propaga eventos de VOI/windowlevel
        return;
      };
      const camListener = (_e: any) => {
        if (!this.propagating || this.applying) return;
        this._maybeExitProgrammatic(viewportId);
        if (this._shouldSuppress(viewportId)) return;
        if (!this._isUserActiveNow(viewportId)) return;
        const viewport = cornerstoneViewportService.getCornerstoneViewport?.(viewportId) || null;
        const cam = viewport && (viewport as any).getCamera ? (viewport as any).getCamera() : null;
        if (!cam) return;
        const slim = this._slimCamera(cam);
        const viewportSlot = this._getViewportSlot(viewportId);
        const orientation = this._orientationFromCamera(cam);
        if (orientation) this._lastViewportOrientation[viewportId] = orientation;
        const cameraForSend = this._stripZoomPanFromCamera(slim);
        this.send({
          type: 'camera',
          viewportId,
          camera: cameraForSend,
          viewportSlot,
          orientation,
          userInitiated: true,
        });
        try {
          const z = (viewport as any)?.getZoom?.();
          const p = (viewport as any)?.getPan?.();
          const ps = typeof cam?.parallelScale === 'number' ? cam.parallelScale : undefined;
          const pos = Array.isArray(cam?.position)
            ? (cam.position as [number, number, number])
            : undefined;
          const fp = Array.isArray(cam?.focalPoint)
            ? (cam.focalPoint as [number, number, number])
            : undefined;
          if (typeof z === 'number' || (p && typeof p.x === 'number') || typeof ps === 'number') {
            const last = this._lastZoomPan[viewportId] || {};
            const panChanged = p && (!last.pan || p.x !== last.pan.x || p.y !== last.pan.y);
            const zoomChanged = typeof z === 'number' && z !== last.zoom;
            const psChanged = typeof ps === 'number' && ps !== last.parallelScale;
            const changed = panChanged || zoomChanged || psChanged;
            if (changed) {
              this._lastZoomPan[viewportId] = {
                zoom: z,
                pan: p,
                parallelScale: ps,
                position: pos,
                focalPoint: fp,
              };
            }
          }
        } catch {}
      };
      const scrollListener = (_e: any) => {
        if (!this.propagating || this.applying) return;
        this._maybeExitProgrammatic(viewportId);
        this._markUserActive(viewportId, false);
        if (this._shouldSuppress(viewportId, 'scroll')) return;
        if (!this._isUserActiveNow(viewportId)) return;
        try {
          const vp = cornerstoneViewportService.getCornerstoneViewport?.(viewportId) || null;
          if (!vp) return;
          const getIdx = (vp as any).getCurrentImageIdIndex;
          const getSlice = (vp as any).getSliceIndex;
          let idx: number | null = null;
          if (typeof getIdx === 'function') idx = getIdx.call(vp);
          else if (typeof getSlice === 'function') idx = getSlice.call(vp);
          if (typeof idx === 'number') {
            const vp = cornerstoneViewportService.getCornerstoneViewport?.(viewportId);
            const cam = vp?.getCamera?.();
            const orientation = this._orientationFromCamera(cam);
            if (orientation) this._lastViewportOrientation[viewportId] = orientation;
            const viewportSlot = this._getViewportSlot(viewportId);
            this.send({
              type: 'scroll',
              viewportId,
              imageIndex: idx,
              viewportSlot,
              orientation,
              userInitiated: true,
            });
          }
        } catch {}
      };
      try {
        el.addEventListener(csEnums.Events.VOI_MODIFIED as any, voiListener as any, true);
        el.addEventListener(csEnums.Events.CAMERA_MODIFIED as any, camListener as any, true);
        el.addEventListener(
          csEnums.Events.STACK_VIEWPORT_SCROLL as any,
          scrollListener as any,
          true
        );
        el.addEventListener(csEnums.Events.STACK_NEW_IMAGE as any, scrollListener as any, true);
        el.addEventListener(csEnums.Events.VOLUME_NEW_IMAGE as any, scrollListener as any, true);
      } catch {
        el.addEventListener('CORNERSTONE_VOI_MODIFIED', voiListener as any, true);
        el.addEventListener('CORNERSTONE_CAMERA_MODIFIED', camListener as any, true);
        el.addEventListener('CORNERSTONE_STACK_VIEWPORT_SCROLL', scrollListener as any, true);
        el.addEventListener('CORNERSTONE_STACK_NEW_IMAGE', scrollListener as any, true);
        el.addEventListener('CORNERSTONE_VOLUME_NEW_IMAGE', scrollListener as any, true);
      }

      const samplingState: any = { raf: 0, lastCam: null, lastIdx: null, active: false };
      const sample = () => {
        if (!samplingState.active) return;
        try {
          const vp = cornerstoneViewportService.getCornerstoneViewport?.(viewportId) || null;
          if (vp && this.propagating && !this.applying) {
            this._maybeExitProgrammatic(viewportId);
            if (this._shouldSuppress(viewportId) || !this._isUserActiveNow(viewportId)) {
              samplingState.raf = requestAnimationFrame(sample);
              return;
            }
            // camera
            try {
              const cam = vp.getCamera?.();
              if (cam) {
                const slim = this._slimCamera(cam);
                if (!this._cameraEqual(samplingState.lastCam, slim)) {
                  samplingState.lastCam = slim;
                  const viewportSlot = this._getViewportSlot(viewportId);
                  const orientation = this._orientationFromCamera(cam);
                  if (orientation) this._lastViewportOrientation[viewportId] = orientation;
                  const cameraForSend = this._stripZoomPanFromCamera(slim);
                  this.send({
                    type: 'camera',
                    viewportId,
                    camera: cameraForSend,
                    viewportSlot,
                    orientation,
                    userInitiated: true,
                  });
                }
              }
            } catch {}
            // zoom/pan
            try {
              const z = (vp as any)?.getZoom?.();
              const p = (vp as any)?.getPan?.();
              const cam = vp.getCamera?.();
              const ps = typeof cam?.parallelScale === 'number' ? cam.parallelScale : undefined;
              const pos = Array.isArray(cam?.position)
                ? (cam.position as [number, number, number])
                : undefined;
              const fp = Array.isArray(cam?.focalPoint)
                ? (cam.focalPoint as [number, number, number])
                : undefined;
              const last = this._lastZoomPan[viewportId] || {};
              const panChanged = p && (!last.pan || p.x !== last.pan.x || p.y !== last.pan.y);
              const zoomChanged = typeof z === 'number' && z !== last.zoom;
              const psChanged = typeof ps === 'number' && ps !== last.parallelScale;
              const changed = panChanged || zoomChanged || psChanged;
              if (changed) {
                this._lastZoomPan[viewportId] = {
                  zoom: z,
                  pan: p,
                  parallelScale: ps,
                  position: pos,
                  focalPoint: fp,
                };
              }
            } catch {}
            // scroll / slice
            try {
              const getIdx = (vp as any).getCurrentImageIdIndex;
              const getSlice = (vp as any).getSliceIndex;
              let idx: number | null = null;
              if (typeof getIdx === 'function') idx = getIdx.call(vp);
              else if (typeof getSlice === 'function') idx = getSlice.call(vp);
              if (typeof idx === 'number' && idx !== samplingState.lastIdx) {
                samplingState.lastIdx = idx;
                this._markUserActive(viewportId, false);
                const camNow = vp.getCamera?.();
                const orientation = this._orientationFromCamera(camNow);
                if (orientation) this._lastViewportOrientation[viewportId] = orientation;
                const viewportSlot = this._getViewportSlot(viewportId);
                this.send({
                  type: 'scroll',
                  viewportId,
                  imageIndex: idx,
                  viewportSlot,
                  orientation,
                  userInitiated: true,
                });
              }
            } catch {}
          }
        } catch {}
        samplingState.raf = requestAnimationFrame(sample);
      };
      const startSampling = () => {
        if (samplingState.active) return;
        this._scheduleProgrammaticExit(viewportId);
        this._markUserActive(viewportId, false);
        samplingState.active = true;
        samplingState.raf = requestAnimationFrame(sample);
      };
      const stopSampling = () => {
        samplingState.active = false;
        if (samplingState.raf) cancelAnimationFrame(samplingState.raf);
        samplingState.raf = 0;
      };
      el.addEventListener('pointerdown', startSampling, true);
      el.addEventListener('pointerup', stopSampling, true);
      el.addEventListener('pointerleave', stopSampling, true);
      el.addEventListener('pointercancel', stopSampling, true);
      const wheelListener = () => {
        this._scheduleProgrammaticExit(viewportId);
        this._markUserActive(viewportId, false);
      };
      el.addEventListener('wheel', wheelListener, { passive: true, capture: true } as any);

      const globalScrollListener = () => {
        this._scheduleProgrammaticExit(viewportId);
        this._markUserActive(viewportId, false);
      };
      el.addEventListener('scroll', globalScrollListener, { passive: true, capture: true } as any);

      this.unsubscribes.push(() => {
        try {
          el.removeEventListener(csEnums.Events.VOI_MODIFIED as any, voiListener as any, true);
          el.removeEventListener(csEnums.Events.CAMERA_MODIFIED as any, camListener as any, true);
          el.removeEventListener(
            csEnums.Events.STACK_VIEWPORT_SCROLL as any,
            scrollListener as any,
            true
          );
          el.removeEventListener(
            csEnums.Events.STACK_NEW_IMAGE as any,
            scrollListener as any,
            true
          );
          el.removeEventListener(
            csEnums.Events.VOLUME_NEW_IMAGE as any,
            scrollListener as any,
            true
          );
        } catch {
          el.removeEventListener('CORNERSTONE_VOI_MODIFIED', voiListener as any, true);
          el.removeEventListener('CORNERSTONE_CAMERA_MODIFIED', camListener as any, true);
          el.removeEventListener('CORNERSTONE_STACK_VIEWPORT_SCROLL', scrollListener as any, true);
          el.removeEventListener('CORNERSTONE_STACK_NEW_IMAGE', scrollListener as any, true);
          el.removeEventListener('CORNERSTONE_VOLUME_NEW_IMAGE', scrollListener as any, true);
        }
        el.removeEventListener('pointerdown', startSampling, true);
        el.removeEventListener('pointerup', stopSampling, true);
        el.removeEventListener('pointerleave', stopSampling, true);
        el.removeEventListener('pointercancel', stopSampling, true);
        try {
          el.removeEventListener('wheel', wheelListener as any, true as any);
          el.removeEventListener('scroll', globalScrollListener as any, true as any);
        } catch {}
        stopSampling();
      });
    };

    try {
      const ids = cornerstoneViewportService.getViewportIds?.() || [];
      ids.forEach(addForViewport);
    } catch {}

    try {
      const sub = cornerstoneViewportService.subscribe?.(
        'event::cornerstoneViewportService:viewportDataChanged',
        (evt: any) => {
          const id = evt?.viewportId;
          if (id) {
            addForViewport(id);
            this._enterProgrammatic(id);
            this._squelch(id, 800);
          }
        }
      );
      if (sub && sub.unsubscribe) this.unsubscribes.push(() => sub.unsubscribe());
    } catch {}

    if (toolbarService) {
      const handleToolbarUpdate = () => {
        this._flushPendingToolActivation();
        if (!this.propagating || this.applying) return;
        const active = this._pickActiveToolbarItemId();
        if (!active) return;
        if (this._lastSentTool === active) return;
        this._lastSentTool = active;
        this.send({ type: 'tool', activeTool: active });
      };

      const events = [
        toolbarService.EVENTS?.TOOL_BAR_STATE_MODIFIED,
        toolbarService.EVENTS?.TOOL_BAR_MODIFIED,
      ].filter(Boolean);

      const subs = events.map(eventName =>
        toolbarService.subscribe?.(eventName, handleToolbarUpdate)
      );

      subs.forEach(sub => {
        if (sub && sub.unsubscribe) {
          this.unsubscribes.push(() => sub.unsubscribe());
        }
      });
    }

    try {
      const handleGlobalViewportChange = () => {
        const activeViewportId = viewportGridService?.getActiveViewportId?.();
        if (activeViewportId) {
          this._markUserActive(activeViewportId, false);
        }
      };

      if (viewportGridService?.subscribe) {
        const viewportSub = viewportGridService.subscribe(
          'event::viewportGridService:viewportActivated',
          handleGlobalViewportChange
        );
        if (viewportSub && viewportSub.unsubscribe) {
          this.unsubscribes.push(() => viewportSub.unsubscribe());
        }
      }

      if (typeof document !== 'undefined') {
        const documentScrollHandler = () => {
          const activeViewportId = viewportGridService?.getActiveViewportId?.();
          if (activeViewportId) {
            this._markUserActive(activeViewportId, false);
          }
        };
        document.addEventListener('scroll', documentScrollHandler, {
          passive: true,
          capture: true,
        });
        this.unsubscribes.push(() => {
          document.removeEventListener('scroll', documentScrollHandler, true as any);
        });
      }
    } catch {}
  }

  private send(evt: SyncEvent) {
    if (!bridge || !this.propagating) return;
    const key = evt.type;
    const now = Date.now();
    const last = this.lastSent[key];
    const elapsed = typeof last === 'number' ? now - last : Number.POSITIVE_INFINITY;
    if (this.throttleMs > 0 && elapsed < this.throttleMs) {
      const delay = Math.max(this.throttleMs - elapsed, 0) || this.throttleMs;
      const existing = this.pendingSends[key];
      if (existing?.timeout) clearTimeout(existing.timeout);
      this.pendingSends[key] = {
        evt: this._cloneEvent(evt),
        timeout: setTimeout(() => {
          const pending = this.pendingSends[key];
          if (!pending) return;
          delete this.pendingSends[key];
          this._sendNow(pending.evt);
        }, delay),
      };
      return;
    }
    if (this.pendingSends[key]?.timeout) {
      clearTimeout(this.pendingSends[key].timeout!);
      delete this.pendingSends[key];
    }
    this._sendNow(evt);
  }

  private _sendNow(evt: SyncEvent) {
    if (!bridge || !this.propagating) return;
    const key = evt.type;
    this.lastSent[key] = Date.now();
    try {
      try {
        (bridge as any)?.log?.('sync-send', {
          type: evt.type,
          vp: (evt as any).viewportId,
          payload: evt,
        });
      } catch {}
      bridge.broadcastSync?.(evt);
      this.stats.sent[evt.type]++;
      this.updateDebugOverlay();
    } catch {}
  }

  private _clearPendingSends() {
    Object.keys(this.pendingSends).forEach(key => {
      const pending = this.pendingSends[key];
      if (pending?.timeout) clearTimeout(pending.timeout);
      delete this.pendingSends[key];
    });
  }

  private _cloneEvent(evt: SyncEvent): SyncEvent {
    try {
      return JSON.parse(JSON.stringify(evt));
    } catch {
      return evt;
    }
  }

  private applyIncoming(evt: SyncEvent) {
    this.applying = true;
    const { commandsManager } = (window as any) || {};
    try {
      try {
        (bridge as any)?.log?.('sync-incoming', {
          type: evt.type,
          vp: (evt as any).viewportId,
          payload: evt,
        });
      } catch {}
      const { cornerstoneViewportService, viewportGridService, toolbarService } =
        this.services || {};
      const ensureViewport = (id?: string, slot?: number, orientation?: string) => {
        let vp: any = null;
        let pickedId: string | undefined = undefined;
        const ids: string[] = (() => {
          try {
            return cornerstoneViewportService?.getViewportIds?.() || [];
          } catch {
            return [];
          }
        })();

        // slot
        if (typeof slot === 'number') {
          try {
            const st = viewportGridService?.getState?.();
            const vps = st?.viewports || [];
            const target = vps[slot]?.viewportId;
            if (target) {
              const cand = cornerstoneViewportService?.getCornerstoneViewport?.(target);
              if (cand) {
                vp = cand;
                pickedId = target;
              }
            }
          } catch {}
        }

        //  id
        if (!vp && id && cornerstoneViewportService?.getCornerstoneViewport) {
          try {
            const byId = cornerstoneViewportService.getCornerstoneViewport(id);
            if (byId) {
              vp = byId;
              pickedId = id;
            }
          } catch {}
        }

        //orientation
        if (!vp && orientation) {
          try {
            for (const vid of ids) {
              const cand = cornerstoneViewportService.getCornerstoneViewport(vid) as any;
              const cam = cand?.getCamera?.();
              const ori = this._orientationFromCamera(cam);
              if (ori && ori === orientation) {
                vp = cand;
                pickedId = vid;
                break;
              }
            }
          } catch {}
        }

        // active viewport
        if (!vp) {
          const activeId = viewportGridService?.getActiveViewportId?.();
          if (activeId && cornerstoneViewportService?.getCornerstoneViewport) {
            try {
              const activeVp = cornerstoneViewportService.getCornerstoneViewport(activeId);
              if (activeVp) {
                vp = activeVp;
                pickedId = activeId;
              }
            } catch {}
          }
        }

        if (!vp) {
          try {
            for (const vid of ids) {
              const cand = cornerstoneViewportService.getCornerstoneViewport(vid);
              if (cand) {
                vp = cand;
                pickedId = vid;
                break;
              }
            }
          } catch {}
        }

        try {
          (bridge as any)?.log?.('sync-pick', { requested: { id, slot, orientation }, pickedId });
        } catch {}
        return { id: pickedId, vp };
      };
      const willSkipApply = (pickedVp: any, pickedId?: string) =>
        !!(pickedId && this._programmatic[pickedId]);
      const _orientationMatches = (vp: any, expected?: string) => {
        try {
          if (!expected) return true;
          const cam = vp?.getCamera?.();
          const local = this._orientationFromCamera(cam);
          if (!local) {
            return false;
          }
          return local === expected;
        } catch {
          return false;
        }
      };

      const _isDifferentOrientation = (vp: any, expected?: string) => {
        try {
          if (!expected) return false;
          const cam = vp?.getCamera?.();
          const local = this._orientationFromCamera(cam);
          if (!local) return true;
          return local !== expected;
        } catch {
          return true;
        }
      };

      const _resetViewportIfOrientationChanged = (pickedId?: string, vp?: any) => {
        if (!pickedId || !vp) return;
        try {
          const cam = vp?.getCamera?.();
          const currentOrientation = this._orientationFromCamera(cam);
          const lastOrientation = this._lastViewportOrientation[pickedId];

          if (lastOrientation && currentOrientation && lastOrientation !== currentOrientation) {
            if (typeof vp.resetCamera === 'function') {
              vp.resetCamera();
              vp.render?.();
            }
          }

          if (currentOrientation) {
            this._lastViewportOrientation[pickedId] = currentOrientation;
          }
        } catch (e) {
          if (this.debug) {
            try {
              (bridge as any)?.log?.('sync-orientation-check-error', { e });
            } catch {}
          }
        }
      };
      const mustBeUserInitiated =
        evt?.type === 'voi' ||
        evt?.type === 'camera' ||
        evt?.type === 'zoompan' ||
        evt?.type === 'scroll';
      if (mustBeUserInitiated && !(evt as any).userInitiated) {
        return;
      }
      switch (evt?.type) {
        case 'voi': {
          try {
            const activeId = this.services?.viewportGridService?.getActiveViewportId?.();
            if (activeId) {
              if (this._programmatic[activeId]) {
                if ((evt as any).userInitiated) {
                  this._exitProgrammatic(activeId);
                }
                if (this._programmatic[activeId]) break;
              }
              const vp =
                this.services?.cornerstoneViewportService?.getCornerstoneViewport?.(activeId);
              if (vp && _isDifferentOrientation(vp, (evt as any).orientation)) {
                if (this.debug) {
                  try {
                    (bridge as any)?.log?.('sync-voi-skip-different-orientation', {
                      orientation: (evt as any).orientation,
                    });
                  } catch {}
                }
                break;
              }
              if (vp && !_orientationMatches(vp, (evt as any).orientation)) break;
            }
          } catch {}
          // commandsManager?.run?.('setWindowLevel', {
          //   windowWidth: evt.range.upper - evt.range.lower,
          //   windowCenter: (evt.range.upper + evt.range.lower) / 2,
          // });
          // break;
        }
        case 'camera': {
          const { id: pickedId, vp } = ensureViewport(
            evt.viewportId,
            (evt as any).viewportSlot,
            (evt as any).orientation
          );
          if (pickedId && this._programmatic[pickedId] && (evt as any).userInitiated) {
            this._exitProgrammatic(pickedId);
          }
          if (!vp || willSkipApply(vp, pickedId)) break;

          _resetViewportIfOrientationChanged(pickedId, vp);

          if (_isDifferentOrientation(vp, (evt as any).orientation)) {
            break;
          }
          if (!_orientationMatches(vp, (evt as any).orientation)) break;
          if (vp) {
            try {
              const nextCam = { ...(evt as any).camera };
              vp.setCamera?.(nextCam);
              vp.render?.();
            } catch (e) {}
          }
          break;
        }
        //'zoompan' desabilitados.

        case 'scroll': {
          const { id: pickedId, vp } = ensureViewport(
            evt.viewportId,
            (evt as any).viewportSlot,
            (evt as any).orientation
          );
          if (pickedId && this._programmatic[pickedId] && (evt as any).userInitiated) {
            this._exitProgrammatic(pickedId);
          }
          if (!vp || willSkipApply(vp, pickedId)) break;

          _resetViewportIfOrientationChanged(pickedId, vp);

          if (_isDifferentOrientation(vp, (evt as any).orientation)) {
            if (this.debug)
              console.debug(
                '[Sync][SCROLL apply] skip - different orientation',
                (evt as any).orientation
              );
            break;
          }
          if (!_orientationMatches(vp, (evt as any).orientation)) break;
          if (vp && typeof evt.imageIndex === 'number') {
            try {
              if (typeof (vp as any).setImageIdIndex === 'function') {
                (vp as any).setImageIdIndex(evt.imageIndex);
                (vp as any).render?.();
                if (this.debug) {
                  try {
                    (bridge as any)?.log?.('sync-scroll-setImageIdIndex', {
                      imageIndex: evt.imageIndex,
                    });
                  } catch {}
                }
              } else if (typeof (vp as any).setSlice === 'function') {
                (vp as any).setSlice(evt.imageIndex);
                (vp as any).render?.();
                if (this.debug) {
                  try {
                    (bridge as any)?.log?.('sync-scroll-setSlice', { imageIndex: evt.imageIndex });
                  } catch {}
                }
              }
            } catch {}
          }
          break;
        }
        case 'tool': {
          this._lastSentTool = evt.activeTool;
          const result = this._applyToolById(evt.activeTool);
          if (!result.applied) {
            this._pendingToolActivation = evt.activeTool || null;
            if (this.debug) {
              try {
                (bridge as any)?.log?.('sync-tool-pending-toolbar', { activeTool: evt.activeTool });
              } catch {}
            }
          } else if (!result.viaToolbar) {
            this._pendingToolActivation = evt.activeTool || null;
          }
          break;
        }
      }
      if (evt?.type) {
        this.stats.recv[evt.type]++;
        this.updateDebugOverlay();
      }
    } catch {
    } finally {
      this.applying = false;
    }
  }

  private _slimCamera(cam: any) {
    if (!cam) return cam;
    const {
      position,
      focalPoint,
      parallelScale,
      viewUp,
      zoom,
      sliceNormal,
      rotation,
      flipHorizontal,
      flipVertical,
    } = cam;
    return {
      position,
      focalPoint,
      parallelScale,
      viewUp,
      zoom,
      sliceNormal,
      rotation,
      flipHorizontal,
      flipVertical,
    };
  }
  private _stripZoomPanFromCamera(cam: any) {
    if (!cam || typeof cam !== 'object') return cam;
    const { viewUp, sliceNormal, rotation, flipHorizontal, flipVertical } = cam as any;
    const out: any = {};
    if (viewUp) out.viewUp = viewUp;
    if (sliceNormal) out.sliceNormal = sliceNormal;
    if (typeof rotation !== 'undefined') out.rotation = rotation;
    if (typeof flipHorizontal !== 'undefined') out.flipHorizontal = flipHorizontal;
    if (typeof flipVertical !== 'undefined') out.flipVertical = flipVertical;
    return out;
  }

  private _cameraEqual(a: any, b: any) {
    if (!a || !b) return false;
    const keys = ['parallelScale', 'zoom', 'rotation', 'flipHorizontal', 'flipVertical'];
    for (const k of keys) if (a[k] !== b[k]) return false;
    const arrEq = (x?: any[], y?: any[]) =>
      Array.isArray(x) &&
      Array.isArray(y) &&
      x.length === y.length &&
      x.every((v, i) => v === y[i]);
    if (!arrEq(a.position, b.position)) return false;
    if (!arrEq(a.focalPoint, b.focalPoint)) return false;
    if (!arrEq(a.viewUp, b.viewUp)) return false;
    if (a.sliceNormal || b.sliceNormal) if (!arrEq(a.sliceNormal, b.sliceNormal)) return false;
    return true;
  }

  private startGlobalPolling() {
    this.stopGlobalPolling();
    try {
      (bridge as any)?.log?.('sync-poll-start', {});
    } catch {}
    const poll = () => {
      if (!this.propagating || this.applying) return;
      try {
        const { cornerstoneViewportService } = this.services || {};
        const ids = cornerstoneViewportService?.getViewportIds?.() || [];
        ids.forEach((viewportId: string) => {
          try {
            const vp = cornerstoneViewportService.getCornerstoneViewport?.(viewportId);
            if (!vp) return;
            if (this._shouldSuppress(viewportId) || !this._isUserActiveNow(viewportId)) return;
            // Camera
            try {
              const cam = vp.getCamera?.();
              if (cam) {
                const slim = this._slimCamera(cam);
                const key = '__last_global_cam__';
                const last = (vp as any)[key];
                if (!this._cameraEqual(last, slim)) {
                  (vp as any)[key] = slim;
                  if (this.debug) {
                    try {
                      (bridge as any)?.log?.('sync-camera-global-capture', { viewportId, slim });
                    } catch {}
                  }
                  const viewportSlot = this._getViewportSlot(viewportId);
                  const orientation = this._orientationFromCamera(cam);
                  if (orientation) this._lastViewportOrientation[viewportId] = orientation;
                  const cameraForSend = this._stripZoomPanFromCamera(slim);
                  this.send({
                    type: 'camera',
                    viewportId,
                    camera: cameraForSend,
                    viewportSlot,
                    orientation,
                    userInitiated: true,
                  });
                }
              }
            } catch {}
            // Zoom/Pan
            try {
              const z = (vp as any)?.getZoom?.();
              const p = (vp as any)?.getPan?.();
              const cam = vp.getCamera?.();
              const ps = typeof cam?.parallelScale === 'number' ? cam.parallelScale : undefined;
              const pos = Array.isArray(cam?.position)
                ? (cam.position as [number, number, number])
                : undefined;
              const fp = Array.isArray(cam?.focalPoint)
                ? (cam.focalPoint as [number, number, number])
                : undefined;
              const keyZ = '__last_global_zoom__';
              const keyP = '__last_global_pan__';
              const keyPS = '__last_global_ps__';
              const lastZ = (vp as any)[keyZ];
              const lastP = (vp as any)[keyP];
              const lastPS = (vp as any)[keyPS];
              const panChanged = p && (!lastP || p.x !== lastP.x || p.y !== lastP.y);
              const zoomChanged = typeof z === 'number' && z !== lastZ;
              const psChanged = typeof ps === 'number' && ps !== lastPS;
              if (zoomChanged || panChanged || psChanged) {
                (vp as any)[keyZ] = z;
                (vp as any)[keyP] = p;
                (vp as any)[keyPS] = ps;
                if (this.debug) {
                  try {
                    (bridge as any)?.log?.('sync-zoomp an-local-only', { viewportId, z, p, ps });
                  } catch {}
                }
                const orientation = this._orientationFromCamera(cam);
                if (orientation) this._lastViewportOrientation[viewportId] = orientation;
              }
            } catch {}
            // Scroll
            try {
              const getIdx = (vp as any).getCurrentImageIdIndex;
              const getSlice = (vp as any).getSliceIndex;
              let idx: number | null = null;
              if (typeof getIdx === 'function') idx = getIdx.call(vp);
              else if (typeof getSlice === 'function') idx = getSlice.call(vp);
              if (typeof idx === 'number') {
                const keyIdx = '__last_global_idx__';
                const lastIdx = (vp as any)[keyIdx];
                if (lastIdx !== idx) {
                  (vp as any)[keyIdx] = idx;
                  if (this.debug) {
                    try {
                      (bridge as any)?.log?.('sync-scroll-global-capture', { viewportId, idx });
                    } catch {}
                  }
                  const camNow = vp.getCamera?.();
                  const orientation = this._orientationFromCamera(camNow);
                  if (orientation) this._lastViewportOrientation[viewportId] = orientation;
                  const viewportSlot = this._getViewportSlot(viewportId);
                  this.send({
                    type: 'scroll',
                    viewportId,
                    imageIndex: idx,
                    viewportSlot,
                    orientation,
                    userInitiated: true,
                  });
                }
              }
            } catch {}
          } catch {}
        });
      } catch {}
    };
    this.globalPollTimer = setInterval(poll, 300);
  }
  private stopGlobalPolling() {
    if (this.globalPollTimer) clearInterval(this.globalPollTimer);
    this.globalPollTimer = null;
  }

  // ---- Debug ----
  private ensureDebugOverlay() {
    if (this.debugOverlayEl || typeof document === 'undefined') return;
    const el = document.createElement('div');
    el.style.cssText =
      'position:fixed;left:4px;bottom:4px;background:rgba(0,0,0,0.6);color:#0f0;font:11px monospace;padding:4px 6px;border-radius:4px;z-index:999999;pointer-events:none;white-space:pre;';
    el.id = 'dv-sync-debug';
    document.body.appendChild(el);
    this.debugOverlayEl = el;
    this.updateDebugOverlay();
  }
  private removeDebugOverlay() {
    if (this.debugOverlayEl) {
      try {
        this.debugOverlayEl.remove();
      } catch {}
      this.debugOverlayEl = null;
    }
  }
  private updateDebugOverlay() {
    if (!this.debug || !this.debugOverlayEl) return;
    const { sent, recv } = this.stats;
    this.debugOverlayEl.textContent = `S voi:${sent.voi} cam:${sent.camera} zp:${sent.zoompan} scr:${sent.scroll}\nR voi:${recv.voi} cam:${recv.camera} zp:${recv.zoompan} scr:${recv.scroll}`;
  }

  private _squelch(viewportId: string, ms = 800) {
    try {
      (bridge as any)?.log?.('sync-squelch', { viewportId, ms });
    } catch {}
    this._quietUntil[viewportId] = Date.now() + ms;
  }
  private _markUserActive(viewportId: string, exitProgrammatic = true) {
    this._userActive[viewportId] = true;
    this._quietUntil[viewportId] = 0;
    if (exitProgrammatic) this._exitProgrammatic(viewportId);
    this._lastUserAt[viewportId] = Date.now();
    try {
      (bridge as any)?.log?.('sync-user-active', { viewportId });
    } catch {}
  }
  private _enterProgrammatic(viewportId: string) {
    this._programmatic[viewportId] = true;
    this._userActive[viewportId] = false;
    this._pendingProgrammaticExit[viewportId] = false;
    try {
      (bridge as any)?.log?.('sync-programmatic-enter', { viewportId });
    } catch {}
  }
  private _exitProgrammatic(viewportId: string) {
    this._programmatic[viewportId] = false;
    this._pendingProgrammaticExit[viewportId] = false;
    try {
      (bridge as any)?.log?.('sync-programmatic-exit', { viewportId });
    } catch {}
  }
  private _scheduleProgrammaticExit(viewportId: string) {
    this._pendingProgrammaticExit[viewportId] = true;
  }
  private _maybeExitProgrammatic(viewportId: string) {
    if (!viewportId) return;
    if (!this._programmatic[viewportId]) return;
    if (!this._pendingProgrammaticExit[viewportId]) return;
    this._exitProgrammatic(viewportId);
  }
  private _shouldSuppress(viewportId?: string, eventType?: string): boolean {
    if (!viewportId) return false;
    const until = this._quietUntil[viewportId] || 0;
    if (until && Date.now() < until) return true;
    if (this._programmatic[viewportId]) return true;
    if (eventType === 'scroll') {
      return false;
    }
    // Require at least one explicit interaction on this viewport after (re)attach
    if (!this._userActive[viewportId]) return true;
    return false;
  }
  private _anyUserActive(): boolean {
    try {
      return Object.values(this._userActive).some(Boolean);
    } catch {
      return false;
    }
  }
  private _isUserActiveNow(viewportId: string): boolean {
    const last = (this._lastUserAt && this._lastUserAt[viewportId]) || 0;
    const grace = (this as any)._userGraceMs || 600;
    return !!(this._userActive && this._userActive[viewportId]) && Date.now() - last <= grace;
  }

  private _getViewportSlot(viewportId?: string): number | undefined {
    try {
      if (!viewportId) return undefined;
      const { viewportGridService } = this.services || {};
      const st = viewportGridService?.getState?.();
      const arr = st?.viewports || [];
      const idx = Array.isArray(arr) ? arr.findIndex((v: any) => v?.viewportId === viewportId) : -1;
      return idx >= 0 ? idx : undefined;
    } catch {
      return undefined;
    }
  }
  private _orientationFromCamera(cam?: any): string | undefined {
    try {
      const sourceVector = Array.isArray(cam?.viewPlaneNormal)
        ? cam.viewPlaneNormal
        : Array.isArray(cam?.sliceNormal)
          ? cam.sliceNormal
          : Array.isArray(cam?.viewUp)
            ? cam.viewUp
            : undefined;
      const n = this._normalizeVector(sourceVector);
      if (!n) return undefined;

      const referenceVectors: Record<string, [number, number, number]> = {
        axial: [0, 0, 1],
        coronal: [0, 1, 0],
        sagittal: [1, 0, 0],
      };

      let bestLabel: string | undefined;
      let bestScore = -Infinity;
      let runnerUpScore = -Infinity;

      for (const [label, axis] of Object.entries(referenceVectors)) {
        const score = Math.abs(n[0] * axis[0] + n[1] * axis[1] + n[2] * axis[2]);
        if (score > bestScore) {
          runnerUpScore = bestScore;
          bestScore = score;
          bestLabel = label;
        } else if (score > runnerUpScore) {
          runnerUpScore = score;
        }
      }

      const ALIGNMENT_THRESHOLD = 0.75;
      const AMBIGUITY_THRESHOLD = 0.1;

      if (!bestLabel) return undefined;
      if (bestScore < ALIGNMENT_THRESHOLD) return undefined;
      if (runnerUpScore > -Infinity && bestScore - runnerUpScore < AMBIGUITY_THRESHOLD)
        return undefined;

      return bestLabel;
    } catch {
      return undefined;
    }
  }

  private _normalizeVector(vec?: any): [number, number, number] | undefined {
    if (!Array.isArray(vec) || vec.length !== 3) return undefined;
    const magnitude = Math.hypot(vec[0], vec[1], vec[2]);
    if (!magnitude || !Number.isFinite(magnitude)) return undefined;
    return [vec[0] / magnitude, vec[1] / magnitude, vec[2] / magnitude];
  }

  private _pickActiveToolbarItemId(): string | undefined {
    try {
      const toolbarService = this.services?.toolbarService;
      const state = toolbarService?.state;
      const buttons: Record<string, any> | undefined = state?.buttons;
      const sections: Record<string, string[]> | undefined = state?.buttonSections;
      if (!buttons) return undefined;
      if (sections) {
        for (const ids of Object.values(sections)) {
          if (!Array.isArray(ids)) continue;
          for (const id of ids) {
            const btn = buttons[id];
            if (btn?.props?.isActive) return id;
          }
        }
      }
      for (const [id, btn] of Object.entries(buttons)) {
        if (btn?.props?.isActive) return id;
      }
      return undefined;
    } catch {
      return undefined;
    }
  }

  private _toolbarHasButton(buttonId?: string): boolean {
    try {
      if (!buttonId) return false;
      const toolbarService = this.services?.toolbarService;
      const state = toolbarService?.state;
      if (!state?.buttons) return false;
      return !!state.buttons[buttonId];
    } catch {
      return false;
    }
  }

  private _applyToolById(toolId?: string): { applied: boolean; viaToolbar: boolean } {
    const outcome = { applied: false, viaToolbar: false };
    if (!toolId) {
      return outcome;
    }

    const commandsManager =
      this.commandsManager ||
      (typeof window !== 'undefined' ? (window as any)?.commandsManager : null);

    const collectToolState = () => {
      const snapshot: {
        toolbarActive?: string;
        activePrimaryTools?: Record<string, string | undefined>;
      } = {};

      try {
        snapshot.toolbarActive = this._pickActiveToolbarItemId();
      } catch {}

      try {
        const toolGroupService = this.services?.toolGroupService || this.services?.ToolGroupService;
        const toolGroupIds: string[] | undefined = toolGroupService?.getToolGroupIds?.();
        if (Array.isArray(toolGroupIds) && toolGroupIds.length) {
          snapshot.activePrimaryTools = {};
          toolGroupIds.forEach(groupId => {
            try {
              const activeFromService =
                toolGroupService?.getActivePrimaryMouseButtonTool?.(groupId);
              const toolGroup = toolGroupService?.getToolGroup?.(groupId);
              const activeFromGroup = toolGroup?.getActivePrimaryMouseButtonTool?.();
              snapshot.activePrimaryTools![groupId] = activeFromService || activeFromGroup;
            } catch {}
          });
        }
      } catch {}

      return snapshot;
    };

    const logApply = (
      status: 'success' | 'queued',
      via: 'toolbar' | 'command' | 'none',
      extra?: any
    ) => {
      try {
        const state = collectToolState();
        (bridge as any)?.log?.('sync-tool-apply', {
          toolId,
          status,
          via,
          pendingTool: this._pendingToolActivation,
          ...state,
          ...extra,
        });
      } catch {}
    };

    try {
      if (this._toolbarHasButton(toolId)) {
        const toolbarService = this.services?.toolbarService;
        const viewportGridService = this.services?.viewportGridService;
        const toolGroupService = this.services?.toolGroupService || this.services?.ToolGroupService;

        const viewportId =
          viewportGridService?.getActiveViewportId?.() ??
          viewportGridService?.getState?.()?.activeViewportId;

        let toolGroupId: string | undefined;
        try {
          if (viewportId && toolGroupService?.getToolGroupForViewport) {
            const tg = toolGroupService.getToolGroupForViewport(viewportId);
            toolGroupId = tg?.id;
          }
        } catch {}

        toolbarService?.recordInteraction?.(toolId, {
          viewportId,
          toolGroupId,
          refreshProps: {
            viewportId,
            toolGroupId,
          },
        });

        let commandFallbackUsed = false;
        let activePrimaryAfterToolbar: string | undefined;
        try {
          if (toolGroupService && toolGroupId) {
            activePrimaryAfterToolbar =
              toolGroupService.getActivePrimaryMouseButtonTool?.(toolGroupId);
            if (activePrimaryAfterToolbar !== toolId && commandsManager?.run) {
              commandsManager.run({
                commandName: 'setToolActiveToolbar',
                commandOptions: {
                  toolName: toolId,
                  toolGroupIds: toolGroupId ? [toolGroupId] : undefined,
                },
                context: 'CORNERSTONE',
              });
              commandFallbackUsed = true;
            } else if (activePrimaryAfterToolbar !== toolId && commandsManager?.runCommand) {
              commandsManager.runCommand(
                'setToolActiveToolbar',
                { toolName: toolId, toolGroupIds: toolGroupId ? [toolGroupId] : undefined },
                'CORNERSTONE'
              );
              commandFallbackUsed = true;
            }
          }
        } catch {}

        outcome.applied = true;
        outcome.viaToolbar = true;
        logApply('success', 'toolbar', {
          commandFallback: commandFallbackUsed,
          postToolbarActive: activePrimaryAfterToolbar,
        });
        return outcome;
      }
    } catch (e) {
      if (this.debug) console.warn('[Sync][TOOL apply] toolbar path failed', e);
    }

    try {
      if (commandsManager?.run) {
        commandsManager.run({
          commandName: 'setToolActiveToolbar',
          commandOptions: { toolName: toolId },
          context: 'CORNERSTONE',
        });
        outcome.applied = true;
        logApply('success', 'command');
      } else if (commandsManager?.runCommand) {
        commandsManager.runCommand('setToolActiveToolbar', { toolName: toolId }, 'CORNERSTONE');
        outcome.applied = true;
        logApply('success', 'command');
      }
    } catch (e) {
      if (this.debug) console.warn('[Sync][TOOL apply] command path failed', e);
    }

    if (!outcome.applied) {
      logApply('queued', 'none', { toolbarReady: this._toolbarHasButton(toolId) });
    }

    return outcome;
  }

  private _flushPendingToolActivation() {
    if (!this._pendingToolActivation) return;
    const pending = this._pendingToolActivation;
    const applied = this._applyToolById(pending);
    if (applied.applied && applied.viaToolbar) {
      this._pendingToolActivation = null;
    }
  }
}

export default ElectronSyncService;
